import Cookies from "universal-cookie";

import { ATTRIBUTION_MAX_AGE_SECONDS } from "./build-attribution";
import type { Attribution } from "./attribution.types";
import { hasTrackingConsent } from "@/lib/analytics/tracking-consent";

/** Nome legado, lido pelo checkout em `services/billing/index.ts`. Não renomear. */
export const FROM_TAG_COOKIE = "@tami/fromTag";
/** Payload completo de origem, incluindo UTMs. */
export const ATTRIBUTION_COOKIE = "@tami/attribution";

/**
 * Por quanto tempo a origem sobrevive.
 *
 * Atribuição não é cookie essencial: apagá-la não quebra nada do que o visitante
 * pediu — quebra a nossa capacidade de saber quem indicou. Por isso quem recusa
 * perde a janela de 90 dias.
 *
 * Mas não perde tudo: `undefined` vira cookie de sessão, que morre ao fechar o
 * navegador e ainda é enviado ao servidor. Quem clicou no link de um creator e
 * assina na mesma visita continua gerando comissão — isso é a transação que a
 * própria pessoa iniciou. O que cai é só a lembrança de 90 dias.
 */
export const resolveAttributionMaxAge = (
  podeRastrear: boolean,
): number | undefined =>
  podeRastrear ? ATTRIBUTION_MAX_AGE_SECONDS : undefined;

const buildCookieOptions = (maxAge: number | undefined) => ({
  path: "/",
  ...(maxAge === undefined ? {} : { maxAge }),
  sameSite: "lax" as const,
  secure: window.location.protocol === "https:",
});

/** Lê a origem já gravada, ou `null` se a pessoa chegou sem nenhum sinal. */
export function readAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;

  const cookies = new Cookies(null, { path: "/" });
  return cookies.get<Attribution | undefined>(ATTRIBUTION_COOKIE) ?? null;
}

/**
 * Grava a origem no browser. Primeira origem vence: se já existe atribuição,
 * não sobrescreve — quem trouxe a pessoa foi quem trouxe, não a última aba aberta.
 */
export function persistAttribution(attribution: Attribution): void {
  if (typeof window === "undefined") return;

  const cookies = new Cookies(null, { path: "/" });
  if (cookies.get(ATTRIBUTION_COOKIE)) return;

  const options = buildCookieOptions(
    resolveAttributionMaxAge(hasTrackingConsent()),
  );

  cookies.set(ATTRIBUTION_COOKIE, attribution, options);

  if (attribution.fromTag) {
    cookies.set(FROM_TAG_COOKIE, attribution.fromTag, options);
  }
}

/**
 * Rebaixa uma origem já gravada de 90 dias para a sessão atual.
 *
 * Chamado quando a pessoa clica em "Recusar" depois de já ter navegado: sem
 * isso o cookie de 90 dias escrito antes da recusa continuaria lá, e a recusa
 * seria só de fachada.
 */
export function downgradeAttributionToSession(): void {
  if (typeof window === "undefined") return;

  const cookies = new Cookies(null, { path: "/" });
  const attribution = cookies.get<Attribution | undefined>(ATTRIBUTION_COOKIE);
  if (!attribution) return;

  const options = buildCookieOptions(undefined);

  cookies.set(ATTRIBUTION_COOKIE, attribution, options);

  if (attribution.fromTag) {
    cookies.set(FROM_TAG_COOKIE, attribution.fromTag, options);
  }
}
